import moment from 'moment';
import { fromNow } from '@ubeya/shared/utils/time';
import {
  HELP_DESK_STATUS_OPTIONS,
  HELP_DESK_MESSAGE_TYPES,
  HELP_DESK_STATUSES
} from '@ubeya/shared/constants/helpDesk';
import { ALLOWED_VIDEO_EXTENSIONS, ALLOWED_WORD_EXTENSIONS, ALLOWED_EXCEL_EXTENSIONS } from './helpDeskConstants';

const IMAGE_EXTENSIONS = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'svg'];
export const MAX_VISIBLE_IMAGES = 4;

const getFileExtension = (item) => (item.fileName || item.name || '').split('.').pop().toLowerCase();
const getMimeType = (item) => item.mimeType || item.type || '';

export const isImageFile = (item) =>
  getMimeType(item).startsWith('image/') || IMAGE_EXTENSIONS.includes(getFileExtension(item));

export const isPdfFile = (item) => getMimeType(item) === 'application/pdf' || getFileExtension(item) === 'pdf';

export const isVideoFile = (item) =>
  getMimeType(item).startsWith('video/') || ALLOWED_VIDEO_EXTENSIONS.includes(getFileExtension(item));

export const getFileType = (item) => {
  if (isPdfFile(item)) return 'pdf';
  const ext = getFileExtension(item);
  if (ALLOWED_WORD_EXTENSIONS.includes(ext)) return 'word';
  if (ALLOWED_EXCEL_EXTENSIONS.includes(ext)) return 'excel';
  return 'other';
};

export const formatFileSize = (bytes) => {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

// region: Date helpers

export const getDateLabel = (dateStr, t) => {
  const date = moment.utc(dateStr).local().startOf('day');
  const today = moment().startOf('day');
  const yesterday = moment().subtract(1, 'day').startOf('day');
  if (date.isSame(today)) return t('today');
  if (date.isSame(yesterday)) return t('yesterday');
  return date.format('DD/MM/YYYY');
};

export const getSnoozedUntilAsLocalMoment = (value) => {
  if (!value) return null;
  const normalized =
    typeof value === 'string'
      ? value
          .trim()
          .replace(' ', 'T')
          .replace(/([+-]\d{2}:?\d{2}|Z)$/i, '') // strip timezone offset so moment.utc() treats it as UTC wall-clock time
      : value;
  const asLocal = moment.utc(normalized).local();
  return asLocal.isValid() ? asLocal : null;
};

export const formatSnoozedUntilLocal = (value) => {
  const asLocal = getSnoozedUntilAsLocalMoment(value);
  return asLocal ? asLocal.format('HH:mm MMM D, YYYY') : null;
};

/**
 * Display label for a snoozed ticket. Future snoozes render as "<relative>, <absolute>"
 * (e.g. "in 2 hours, 14:30 May 18, 2026"); elapsed snoozes (lazy unsnooze hasn't run yet)
 * fall back to "Snoozed until <absolute>". Shared by the ticket list row, the status banner
 * inside the ticket header, and the employee profile's tickets-history cards. Pass
 * `relativeOnly: true` for the compact list-row variant which drops the absolute time.
 */
export const getSnoozedLabel = (snoozedUntil, t, { relativeOnly = false } = {}) => {
  if (!snoozedUntil) return null;
  const localMoment = getSnoozedUntilAsLocalMoment(snoozedUntil);
  if (!localMoment) return null;
  const relative = fromNow(snoozedUntil, t);
  if (relativeOnly) return relative || t('help_desk_snoozed');
  const timeStr = localMoment.format('HH:mm MMM D, YYYY');
  return relative ? `${relative}, ${timeStr}` : `${t('help_desk_snoozed_until')} ${timeStr}`;
};

// region: Message grouping

/**
 * Groups a flat array of messages into items with date separators and unread dividers.
 * @param {Array} messages - The messages array (in chronological order)
 * @returns {Array} - Array of { type: 'date'|'unread'|'message', ... } items
 */
export const groupMessagesByDate = (messages) => {
  if (!messages.length) return [];
  const items = [];
  let lastDate = null;
  let unreadInserted = false;

  messages.forEach((msg) => {
    const msgDate = msg.createdAt ? moment.utc(msg.createdAt).local().format('YYYY-MM-DD') : null;

    if (msgDate && msgDate !== lastDate) {
      items.push({ type: 'date', date: msg.createdAt, key: `date-${msgDate}` });
      lastDate = msgDate;
    }

    if (!unreadInserted && msg.isFirstUnread) {
      items.push({ type: 'unread', key: 'unread-divider' });
      unreadInserted = true;
    }

    items.push({ type: 'message', data: msg, key: `msg-${msg.id}` });
  });

  return items;
};

// region: System event description

// Map enum status value → locale key for its label (e.g. 'CLOSED' → 'help_desk_closed').
// Derived from HELP_DESK_STATUS_OPTIONS so the system-event renderer stays in sync with
// the inbox status select.
const STATUS_LABEL_KEYS = Object.fromEntries(HELP_DESK_STATUS_OPTIONS.map(({ value, labelKey }) => [value, labelKey]));

/**
 * Build a translated description from structured event data.
 */
const buildSystemEventDescription = (parsed, t) => {
  const {
    type,
    actorName,
    actorEmployeeId,
    assigneeName,
    status,
    priority,
    snoozedUntil,
    categoryName,
    resolutionTimeoutHours
  } = parsed;
  const translateEvent = (key, values = {}) => {
    return actorName ? t(`${key}_by`, { ...values, actorName }) : t(key, values);
  };

  switch (type) {
    case 'TICKET_CREATED':
      return translateEvent('help_desk_event_ticket_created');
    case 'TICKET_ASSIGNED':
      return assigneeName
        ? actorName
          ? t('help_desk_event_ticket_assigned_by', { assigneeName, actorName })
          : t('help_desk_event_ticket_assigned', { assigneeName })
        : t('help_desk_event_ticket_assigned_no_name');
    case 'STATUS_CHANGED': {
      if (actorEmployeeId && status === 'CLOSED' && actorName) {
        return t('help_desk_event_employee_confirmed_resolution', { actorName });
      }
      const statusLabel = STATUS_LABEL_KEYS[status] ? t(STATUS_LABEL_KEYS[status]) : null;
      return statusLabel
        ? translateEvent('help_desk_event_status_changed', { status: statusLabel })
        : translateEvent('help_desk_event_status_changed_no_value');
    }
    case 'PRIORITY_CHANGED':
      return priority
        ? translateEvent('help_desk_event_priority_changed', { priority })
        : translateEvent('help_desk_event_priority_changed_no_value');
    case 'REOPENED':
      return translateEvent('help_desk_event_reopened');
    case 'RESOLVED':
      return translateEvent('help_desk_event_resolved');
    case 'UNRESOLVED':
      return actorEmployeeId && actorName
        ? t('help_desk_event_employee_declined_resolution', { actorName })
        : translateEvent('help_desk_event_unresolved');
    case 'AUTO_CLOSED':
      return resolutionTimeoutHours
        ? t('help_desk_event_auto_closed', { hours: resolutionTimeoutHours })
        : t('help_desk_event_auto_closed_no_value');
    case HELP_DESK_STATUSES.SNOOZED:
      return snoozedUntil
        ? translateEvent('help_desk_event_snoozed_until', {
            snoozedUntil: formatSnoozedUntilLocal(snoozedUntil) || snoozedUntil
          })
        : translateEvent('help_desk_event_snoozed');
    case 'CATEGORY_CHANGED':
      return categoryName
        ? translateEvent('help_desk_event_category_changed', { categoryName })
        : translateEvent('help_desk_event_category_changed_no_value');
    case 'BADGE_PENDING_SET':
      return translateEvent('help_desk_event_badge_pending_set');
    case 'BADGE_PENDING_UNSET':
      return translateEvent('help_desk_event_badge_pending_unset');
    default:
      return type ? type.replace(/_/g, ' ').toLowerCase() : '';
  }
};

/**
 * Extract a human-readable description from system event content.
 * Content may be a pre-parsed object (from the transformer) or a raw JSON string.
 * @param {string|Object} content - The system event content
 * @param {Function} t - Translation function
 */
export const getSystemEventDescription = (content, t) => {
  let parsed = content;
  if (typeof content === 'string') {
    try {
      parsed = JSON.parse(content);
    } catch {
      return content;
    }
  }
  if (parsed && typeof parsed === 'object' && parsed.type) {
    return buildSystemEventDescription(parsed, t);
  }
  return String(content ?? '');
};

/**
 * Extract a displayable string from a message's content field.
 * For SYSTEM_EVENT messages the content is a JSON object with a description field.
 * For regular messages it's a plain string.
 */
export const getMessagePreviewContent = (lastMessage, t) => {
  if (!lastMessage?.content) return '';
  const { content, messageType } = lastMessage;
  if (messageType === HELP_DESK_MESSAGE_TYPES.SYSTEM_EVENT) {
    return getSystemEventDescription(content, t);
  }
  return typeof content === 'string' ? content : JSON.stringify(content);
};

//Build a file input's `accept` attribute value from a list of extensions (without dots).
export const toAcceptAttribute = (extensions) => extensions.map((extension) => `.${extension}`).join(',');

/**
 * Format a shift's start–end in the account's time format (empty string if either is missing).
 * Shared by the create-ticket shift picker rows and the step-3 summary breadcrumb.
 */
export const formatShiftTimeRange = (shift, timeFormat = 'HH:mm') =>
  shift?.startTime && shift?.endTime
    ? `${moment.utc(shift.startTime).format(timeFormat)}-${moment.utc(shift.endTime).format(timeFormat)}`
    : '';

// region: Employee info extraction

/**
 * Extract display-ready employee info from the ticket detail response.
 * The backend now bundles employeeName + employeeAvatarUrl onto the detail/list responses,
 * so no separate employee fetch is required for the header/sidepanel skeleton.
 * Used by HelpDeskTicketHeader and HelpDeskSidePanel.
 */
export const extractEmployeeInfo = (ticket) => {
  const employeeName = ticket?.employeeName || '';
  const employeeAvatar = ticket?.employeeAvatarUrl || undefined;
  const employeeFirstName = employeeName ? employeeName.split(' ')[0] : '';
  const employeeLastName = employeeName ? employeeName.split(' ').slice(1).join(' ') : '';
  return { employeeName, employeeAvatar, employeeFirstName, employeeLastName };
};

export const isTicketResolvedPending = (ticket) => !!ticket?.resolvedAt && ticket?.status === HELP_DESK_STATUSES.OPEN;

export const getTicketBranch = ({ branch, branchId, mappedBranches = {} }) => {
  const mappedBranch = mappedBranches[branch?.id || branchId];
  const name = mappedBranch?.name || branch?.name;
  return name ? { name, logo: mappedBranch?.logo } : null;
};
